/**
 * Logic thuần cho nút "Generate all" (260819) — tách khỏi file import antd để jest chạy được.
 *
 * Nút này KHÔNG có ô chọn model riêng: nó chạy theo đúng cấu hình ⚙️ mà người dùng đã đặt ở từng clip.
 * Việc của các hàm dưới là **nói trước** sắp gen bao nhiêu clip và bao nhiêu trong số đó dùng cấu hình
 * đắt hơn mặc định — gen lẻ sai model mất tiền một clip, gen cả tập sai model mất tiền nhân số event.
 */
import type { FilmFlowVersion, FilmVideoEngine } from '../../types/film';
import { optInTuningOf } from './workflowGenerationUtils';

export interface GenTuningLite {
    model?: 'mini' | 'pro' | 'fast' | '2.5';
    resolution?: '480p' | '720p' | '1080p';
}

/** Cấu hình này đắt hơn mặc định (mini · 480p) chưa? 260922 E5-P1: 2.5 luôn đắt hơn (chất lượng cao, tới 30 ảnh tham chiếu). */
export function isCostly(
    t: GenTuningLite | undefined,
    flowVersion?: FilmFlowVersion | string | null,
    videoEngine?: FilmVideoEngine | string | null,
): boolean {
    if (flowVersion === 'sky' && videoEngine === 'SEEDANCE_25') return true;
    if (!t) return false;
    return t.model === 'pro' || t.model === '2.5' || t.resolution === '720p' || t.resolution === '1080p';
}

/** Chỉ giữ những clip THỰC SỰ đặt khác mặc định — gửi cả map rỗng lên server là vô nghĩa. */
export function overridesOf(
    tuningByEvent: Record<string, GenTuningLite>,
    eventIds: string[],
): Record<string, GenTuningLite> {
    const out: Record<string, GenTuningLite> = {};
    for (const id of eventIds) {
        const t = tuningByEvent[id];
        if (t && (t.model || t.resolution)) out[id] = t;
    }
    return out;
}

export interface GenAllSummary {
    label: string;
    /** Câu xác nhận — luôn có số clip, và nói rõ bao nhiêu clip dùng cấu hình riêng. */
    confirm: string;
    danger: boolean;
}

/** Video của plan opt-in: cấu hình ĐÃ LƯU là thứ BE sẽ resolve khi gen cả tập (⚙️ theo lượt bị bỏ qua) — nhãn + có đắt hơn mặc định không. */
export interface SavedGen { label: string; costly: boolean }
export function savedGenOf(ev: { generation?: { engine?: string; model?: string | null; resolution?: string | null } | null } | null | undefined): SavedGen | null {
    const o = optInTuningOf(ev);
    if (!o) return null;
    const is25 = ev?.generation?.engine === 'SEEDANCE_25';
    return { label: `${is25 ? '2.5' : `2.0 ${o.model}`} ${o.resolution}`, costly: is25 || o.model === 'pro' || o.resolution !== '480p' };
}

export function genAllSummary(
    pending: number,
    overrides: Record<string, GenTuningLite>,
    engineByEvent: Record<string, FilmVideoEngine | string | null | undefined> = {},
    flowVersion?: FilmFlowVersion | string | null,
    saved: Record<string, SavedGen> = {},
): GenAllSummary {
    // Đã gen đủ thì nói thẳng, đừng hiện "· 0" rồi để người ta bấm vào một lượt chạy không làm gì.
    if (pending <= 0) return { label: 'Generate all · nothing left', confirm: '', danger: false };
    const savedIds = Object.keys(saved);
    const ids = Object.keys(overrides).filter((id) => !saved[id]); // ⚙️ theo lượt của video đã lưu cấu hình bị BE bỏ qua
    const engine25Ids = Object.entries(engineByEvent)
        .filter(([id, engine]) => !saved[id] && flowVersion === 'sky' && engine === 'SEEDANCE_25')
        .map(([id]) => id);
    const costly = new Set([...ids.filter((id) => isCostly(overrides[id], flowVersion, engineByEvent[id])), ...engine25Ids, ...savedIds.filter((id) => saved[id].costly)]).size;
    const clip = `${pending} clip${pending === 1 ? '' : 's'}`;
    if (!ids.length && !engine25Ids.length && !savedIds.length) {
        return { label: `Generate all · ${pending}`, confirm: `Generate ${clip} at the default mini · 480p?`, danger: false };
    }
    const engine25Note = engine25Ids.length ? ` ${engine25Ids.length} clip gen bằng 2.5 theo engine.` : '';
    const counts = new Map<string, number>();
    for (const id of savedIds) counts.set(saved[id].label, (counts.get(saved[id].label) ?? 0) + 1);
    const savedNote = savedIds.length ? ` Theo cấu hình đã lưu của từng video: ${Array.from(counts.entries()).map(([l, n]) => `${l} ×${n}`).join(' · ')}.` : '';
    const rest = pending - savedIds.length - ids.length - engine25Ids.length;
    const restNote = savedIds.length && rest > 0 ? ` ${rest} clip còn lại dùng mặc định mini · 480p.` : '';
    const details = [
        ...(ids.length ? [`${ids.length} of them use the per-clip setting you picked`] : []),
        ...(costly && (ids.length || engine25Ids.length) ? [`${costly - savedIds.filter((id) => saved[id].costly).length} at a costlier model/resolution than the mini · 480p default`] : []),
    ].filter((d) => !/^0 /.test(d));
    return {
        label: `Generate all · ${pending}${costly ? ` · ${costly}✦` : ''}`,
        confirm: `Generate ${clip}?${details.length ? ` ${details.join(', ')}.` : ''}${savedNote}${engine25Note}${restNote}`,
        danger: costly > 0,
    };
}

/** Cấu hình HIỆU LỰC của lượt Generate-all, dựng từ dữ liệu plan: video opt-in ⇒ cấu hình đã lưu; legacy ⇒ ⚙️ theo lượt / mặc định (giữ nguyên câu cũ). */
export function genAllSummaryForPlan(a: {
    events: Array<{ id: string; videoEngine?: string | null; generation?: { engine?: string; model?: string | null; resolution?: string | null } | null }>;
    pendingIds: string[];
    tuningByEvent: Record<string, GenTuningLite>;
    flowVersion?: FilmFlowVersion | string | null;
}): GenAllSummary & { hasSaved: boolean } {
    const pendingSet = new Set(a.pendingIds);
    const pendingEvents = a.events.filter((e) => pendingSet.has(e.id));
    const saved: Record<string, SavedGen> = {};
    for (const e of pendingEvents) { const s = savedGenOf(e); if (s) saved[e.id] = s; }
    const overrides = overridesOf(a.tuningByEvent, a.pendingIds);
    const engineByEvent = Object.fromEntries(pendingEvents.map((e) => [e.id, e.videoEngine]));
    const s = genAllSummary(a.pendingIds.length, overrides, engineByEvent, a.flowVersion, saved);
    return { ...s, hasSaved: Object.keys(saved).length > 0 };
}

export const genAllTooltipOf = (hasSaved: boolean): string => (hasSaved
    ? 'Runs every remaining event: videos with a saved engine/model/resolution use it; other clips use the ⚙️ setting you picked (or the plan default). Background, ~15 min. Chạy theo cấu hình đã lưu của từng video.'
    : 'Runs every remaining event with the ⚙️ setting you picked on that clip; clips you did not touch use the mini · 480p default. Background, ~15 min.');
